/* Product Gallery — horizontal carousel + zoom viewer (Alpine.js component) */
document.addEventListener('alpine:init', () => {
  Alpine.data('productGallery', (mediaCount) => ({
    activeIndex: 0,
    zoomOpen: false,
    zoomIndex: 0,
    zoomImages: [],
    zoomScale: 1, zoomOriginX: 50, zoomOriginY: 50, zoomPanX: 0, zoomPanY: 0,
    touchStartX: 0, touchStartY: 0, touchMoved: false, touchDragging: false,
    zoomStartPanX: 0, zoomStartPanY: 0, previousBodyOverflow: '',
    galleryTouchX: 0, galleryTouchY: 0, galleryTouchMoved: false,
    lastGalleryTouchAt: 0,

    init() {
      const track = this.$refs.track;
      if (!track) return;

      const slides = track.querySelectorAll('.product-gallery__slide');

      // Collect high-res sources for the zoom viewer.
      this.zoomImages = Array.from(slides)
        .map((s) => {
          const img = s.querySelector('img');
          return img ? img.dataset.zoom || img.src : null;
        })
        .filter(Boolean);

      // Track which slide is centered to highlight the active thumbnail.
      if ('IntersectionObserver' in window && slides.length) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.activeIndex = Number(entry.target.dataset.index || 0);
              }
            });
          },
          { root: track, threshold: 0.6 }
        );
        slides.forEach((slide) => observer.observe(slide));
      }

      // Sync gallery when a variant with its own media is selected.
      window.addEventListener('variant-media-change', (e) => {
        const mediaId = e.detail && e.detail.mediaId;
        if (!mediaId) return;
        slides.forEach((slide, i) => {
          if (slide.dataset.mediaId == mediaId) {
            this.scrollToMedia(i);
          }
        });
      });
    },

    scrollToMedia(index) {
      const track = this.$refs.track;
      if (!track) return;
      const slides = track.querySelectorAll('.product-gallery__slide');
      if (slides[index]) {
        this.activeIndex = index;
        slides[index].scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest'
        });
      }
    },

    next() {
      const track = this.$refs.track;
      if (track) track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
    },

    prev() {
      const track = this.$refs.track;
      if (track) track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
    },

    /* Open the full-screen image on the FIRST mobile tap.
       Touch movement is not treated as a tap, so both horizontal
       photo swipes and vertical page scrolling keep working. */
    galleryTouchStart(event) {
      if (event.touches.length !== 1) {
        this.galleryTouchMoved = true;
        return;
      }
      this.galleryTouchX = event.touches[0].clientX;
      this.galleryTouchY = event.touches[0].clientY;
      this.galleryTouchMoved = false;
    },

    galleryTouchMove(event) {
      if (!event.touches.length) return;
      if (Math.abs(event.touches[0].clientX - this.galleryTouchX) > 9 ||
          Math.abs(event.touches[0].clientY - this.galleryTouchY) > 9) {
        this.galleryTouchMoved = true;
      }
    },

    galleryTouchEnd(event, index) {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      const dx = Math.abs(touch.clientX - this.galleryTouchX);
      const dy = Math.abs(touch.clientY - this.galleryTouchY);
      if (!this.galleryTouchMoved && dx < 10 && dy < 10) {
        // Suppress Safari's follow-up synthetic click (and first-tap hover).
        if (event.cancelable) event.preventDefault();
        this.lastGalleryTouchAt = Date.now();
        this.openZoom(index);
      }
      this.galleryTouchMoved = false;
    },

    galleryClick(event, index) {
      // Mouse click remains immediate, without duplicate touch-triggered opens.
      if (Date.now() - this.lastGalleryTouchAt < 700) {
        event.preventDefault();
        return;
      }
      this.openZoom(index);
    },

    /* Zoom viewer */
    openZoom(index = this.activeIndex) {
      if (this.zoomOpen || !this.zoomImages.length) return;
      this.zoomIndex = this.zoomImages[index] ? index : 0;
      this.resetZoom();
      this.zoomOpen = true;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    },

    closeZoom() {
      this.zoomOpen = false;
      document.body.style.overflow = this.previousBodyOverflow;
      this.resetZoom();
    },

    zoomNext() {
      this.zoomIndex = (this.zoomIndex + 1) % this.zoomImages.length;
      this.resetZoom();
    },

    resetZoom() {
      this.zoomScale = 1;
      this.zoomPanX = 0;
      this.zoomPanY = 0;
      this.zoomOriginX = 50;
      this.zoomOriginY = 50;
    },

    zoomTap(event) {
      if (this.touchMoved) return;
      if (this.zoomScale > 1) { this.resetZoom(); return; }
      const img = this.$refs.zoomImage;
      if (!img) return;
      const rect = img.getBoundingClientRect();
      this.zoomOriginX = Math.max(0, Math.min(100, (event.clientX - rect.left) / rect.width * 100));
      this.zoomOriginY = Math.max(0, Math.min(100, (event.clientY - rect.top) / rect.height * 100));
      this.zoomPanX = 0; this.zoomPanY = 0;
      this.zoomScale = 2.5;
    },

    zoomTouchStart(event) {
      if (event.touches.length !== 1) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.zoomStartPanX = this.zoomPanX;
      this.zoomStartPanY = this.zoomPanY;
      this.touchMoved = false;
      this.touchDragging = true;
    },

    zoomTouchMove(event) {
      if (!this.touchDragging || event.touches.length !== 1) return;
      const dx = event.touches[0].clientX - this.touchStartX;
      const dy = event.touches[0].clientY - this.touchStartY;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) this.touchMoved = true;
      if (this.zoomScale > 1) {
        if (event.cancelable) event.preventDefault();
        const img = this.$refs.zoomImage;
        const maxX = img ? img.clientWidth * (this.zoomScale - 1) / 2 : 0;
        const maxY = img ? img.clientHeight * (this.zoomScale - 1) / 2 : 0;
        this.zoomPanX = Math.max(-maxX, Math.min(maxX, this.zoomStartPanX + dx));
        this.zoomPanY = Math.max(-maxY, Math.min(maxY, this.zoomStartPanY + dy));
      }
    },

    zoomTouchEnd(event) {
      if (!this.touchDragging) return;
      this.touchDragging = false;
      const t = event.changedTouches && event.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - this.touchStartX;
      const dy = t.clientY - this.touchStartY;
      if (this.zoomScale === 1 && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        this.touchMoved = true;
        if (dx < 0) this.zoomNext(); else this.zoomPrev();
      }
    },

    zoomPrev() {
      this.zoomIndex = (this.zoomIndex - 1 + this.zoomImages.length) % this.zoomImages.length;
      this.resetZoom();
    }
  }));
});
